import React, {useState, useEffect, useRef} from 'react';
import MedicalFacility from '@services/medical-facility.service';
import {useRouter} from 'next/router';

export default function HospitalSystem() {
    const {locale} = useRouter();

    const [activeId, setActiveId] = useState(null);
    const [hoverId, setHoverId] = useState(null);
    const [hospitals, setHospitals] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeHospital, setActiveHospital] = useState(null);

    const containerRef = useRef(null);
    const hospitalsRef = useRef([]);

    /* =====================
       LOAD DATA
    ===================== */
    const loadListMedicalFacility = async () => {
        const medicalFacilityService = new MedicalFacility();
        try {
            const data = await medicalFacilityService.getMedicalFacilityHome({
                locale: locale === 'en' ? 'en' : 'vn',
            });

            if (Array.isArray(data)) {
                const mapped = data.map(item => ({
                    id: item.business_id,
                    name: locale === 'en' ? item.business_name_en || item.business_name : item.business_name,
                    desc:
                        locale === 'en'
                            ? item.description_en?.replace(/<[^>]+>/g, '') || item.description?.replace(/<[^>]+>/g, '')
                            : item.description?.replace(/<[^>]+>/g, ''),
                    image: item.business_image || '/bw_image/ic_hospital_system.png',
                }));

                setHospitals(mapped);
                hospitalsRef.current = mapped;

                if (mapped.length > 0) {
                    setActiveId(mapped[0].id);
                    setActiveHospital(mapped[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching medical facilities:', error);
        }
    };

    useEffect(() => {
        loadListMedicalFacility();
    }, [locale]);

    useEffect(() => {
        hospitalsRef.current = hospitals;
    }, [hospitals]);

    /* =====================
       SCROLL HANDLER
    ===================== */
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const cardWidth = container.firstChild?.offsetWidth || 0;
            if (!cardWidth) return;

            const indexScroll = Math.round(container.scrollLeft / (cardWidth + 16));

            const currentHospitals = hospitalsRef.current;
            if (currentHospitals[indexScroll]) {
                const hospital = currentHospitals[indexScroll];
                setActiveIndex(indexScroll);
                setActiveId(hospital.id);
                setActiveHospital(hospital);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    const getCurrentActiveId = () => hoverId ?? activeId;

    /* =====================
       TITLE
    ===================== */
    const title = locale === 'en' ? 'TNH Hospital System' : 'Hệ thống bệnh viện TNH';

    return (
        <section className="hospital-system-main">
            <h2>
                {locale === 'en' ? (
                    <>
                        TNH <span>Hospital System</span>
                    </>
                ) : (
                    <>
                        Hệ thống <span>bệnh viện TNH</span>
                    </>
                )}
            </h2>

            {activeHospital && (
                <p className="hospital-main-desc" key={activeHospital.id}>
                    {activeHospital.desc}
                </p>
            )}

            <div className="hospital-cards" ref={containerRef}>
                {hospitals.map(h => {
                    const isActive = getCurrentActiveId() === h.id;
                    return (
                        <div
                            key={h.id}
                            className={`hospital-system-card ${isActive ? 'active' : ''}`}
                            onMouseEnter={() => setHoverId(h.id)}
                            onMouseLeave={() => setHoverId(null)}
                            onClick={() => {
                                setActiveId(h.id);
                                setActiveHospital(h);
                            }}>
                            <img src={h.image} alt={h.name} />
                            <div className="hospital-overlay">
                                <h3>{h.name?.toUpperCase()}</h3>
                                {isActive && (
                                    <div className="desc">
                                        <p>{h.desc}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hospital-pagination-slider">
                <div
                    className="hospital-pagination-progress"
                    style={{
                        width: `${((activeIndex + 1) / (hospitals.length || 1)) * 100}%`,
                    }}
                />
            </div>
        </section>
    );
}
